import { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar, toast } from '@mint-lib/notifications';
import { Auth } from '@mint-sdk/api-auth';

import { USER_BULK_ACTION_TOAST_DURATION_MS } from '../../../constants/userTableV2.js';
import useBulkTaskSocket from './useBulkTaskSocket.js';

export type BulkActionType =
  | 'activate'
  | 'deactivate'
  | 'sendInvitation'
  | 'remove'
  | 'changeRole';

export type BulkActionRequest =
  | {
      action: Exclude<BulkActionType, 'changeRole'>;
      userIds: number[];
    }
  | {
      action: 'changeRole';
      userIds: number[];
      roleId: number;
    };

type Options = {
  onComplete?: () => void;
};

type TaskState = {
  taskId: string;
  action: BulkActionType;
};

const TERMINAL_STATUSES = new Set<Auth.BulkTaskStatusValue>([
  'completed',
  'failed',
]);

type TFn = (key: string, options?: Record<string, unknown>) => string;

type BulkActionMessages = {
  loadingTitle: string;
  successMessage: string;
};

const getBulkActionMessages = (
  action: BulkActionType,
  t: TFn,
): BulkActionMessages => {
  const messages: Record<BulkActionType, BulkActionMessages> = {
    activate: {
      loadingTitle: t('Activating users...'),
      successMessage: t('Users activated.'),
    },
    deactivate: {
      loadingTitle: t('Deactivating users...'),
      successMessage: t('Users deactivated.'),
    },
    sendInvitation: {
      loadingTitle: t('Sending invitations...'),
      successMessage: t('Invitation sent to users.'),
    },
    changeRole: {
      loadingTitle: t('Updating user roles...'),
      successMessage: t('Users updated.'),
    },
    remove: {
      loadingTitle: t('Removing users...'),
      successMessage: t('Users removed from workspace.'),
    },
  };
  return messages[action];
};

const useUserBulkActions = ({ onComplete }: Options = {}) => {
  const { t } = useTranslation('@myn/permissions');
  const [taskState, setTaskState] = useState<TaskState | null>(null);
  const progressToastId = useRef<string | number | null>(null);
  const activate = Auth.bulkActivateUsers();
  const deactivate = Auth.bulkDeactivateUsers();
  const sendInvitation = Auth.bulkSendInvitation();
  const changeRole = Auth.bulkChangeRole();
  const removeUsers = Auth.bulkRemoveUsers();

  const dismissProgressToast = useCallback(() => {
    if (progressToastId.current === null) return;
    toast.dismiss(progressToastId.current);
    progressToastId.current = null;
  }, []);

  // The pending toast already covers the run, so only a terminal status speaks
  const handleStatus = (status: Auth.BulkTaskStatus) => {
    if (!taskState) return;
    if (!TERMINAL_STATUSES.has(status.status)) return;

    const messages = getBulkActionMessages(taskState.action, t);

    // A partial run still counts as a success. sendInvitation leaves "succeeded" empty, so on a completed run fall back to whatever wasent rejected.
    const succeededCount =
      status.succeeded.length ||
      (status.status === 'completed'
        ? Math.max(
            status.total - status.failed.length - status.skipped.length,
            0,
          )
        : 0);
    const isSuccess = succeededCount > 0;
    dismissProgressToast();
    enqueueSnackbar({
      removable: true,
      variant: isSuccess ? 'success' : 'error',
      title: t(isSuccess ? 'Success' : 'Failure'),
      subTitle: isSuccess
        ? messages.successMessage
        : t('Something went wrong. Please try again'),
      textLayout: 'multiline',
    });
    setTaskState(null);
    onComplete?.();
  };

  const handleSocketError = () => {
    if (!taskState) return;
    dismissProgressToast();
    enqueueSnackbar({
      removable: true,
      variant: 'error',
      title: t('Failure'),
      subTitle: t('Something went wrong. Please try again'),
      textLayout: 'multiline',
    });
    setTaskState(null);
    onComplete?.();
  };

  // Streams the active task's progress via socket.io
  useBulkTaskSocket({
    taskId: taskState?.taskId ?? null,
    onUpdate: handleStatus,
    onError: handleSocketError,
  });

  const runBulkAction = useCallback(
    (request: BulkActionRequest) => {
      if (taskState) return;

      const messages = getBulkActionMessages(request.action, t);

      progressToastId.current = enqueueSnackbar({
        variant: 'info',
        title: messages.loadingTitle,
        textLayout: 'multiline',
        removable: false,
        autoHideDuration: USER_BULK_ACTION_TOAST_DURATION_MS,
      });

      const onSuccess = (data: { taskId: string }) => {
        setTaskState({ taskId: data.taskId, action: request.action });
      };

      const onError = () => {
        dismissProgressToast();
        enqueueSnackbar({
          removable: true,
          variant: 'error',
          title: t('Failure'),
          subTitle: t('Something went wrong. Please try again'),
          textLayout: 'multiline',
        });
      };

      const { userIds } = request;

      switch (request.action) {
        case 'activate':
          activate.mutate({ data: { userIds } }, { onSuccess, onError });
          break;
        case 'deactivate':
          deactivate.mutate({ data: { userIds } }, { onSuccess, onError });
          break;
        case 'sendInvitation':
          sendInvitation.mutate({ data: { userIds } }, { onSuccess, onError });
          break;
        case 'remove':
          removeUsers.mutate({ data: { userIds } }, { onSuccess, onError });
          break;
        case 'changeRole':
          changeRole.mutate(
            { data: { userIds, roleId: request.roleId } },
            { onSuccess, onError },
          );
          break;
      }
    },
    [
      taskState,
      t,
      dismissProgressToast,
      activate,
      deactivate,
      sendInvitation,
      removeUsers,
      changeRole,
    ],
  );

  return { runBulkAction, isInProgress: !!taskState };
};

export default useUserBulkActions;
